import { UPGRADE_DUPLICATES, UPGRADE_PRICES } from '../balance.js';
import { getItem } from '../data/index.js';
import { GameError } from '../errors.js';
import type { GameEvent } from '../events.js';
import { teamPower } from '../formulas/power.js';
import type { BotUser, OwnedItem } from '../state.js';
import { ITEM_TYPES, type ItemType } from '../types.js';

export interface NextLevel {
  needed: number;
  level: number;
  price: number;
}

/** `data/items.ts#itemNextLevel`: cards and fakecoins for the next level. */
export function nextLevel(type: ItemType, owned: Pick<OwnedItem, 'id' | 'level'>): NextLevel {
  const tierIndex = (getItem(type, owned.id)?.tier ?? 1) - 1;
  const dup = UPGRADE_DUPLICATES[tierIndex] ?? (UPGRADE_DUPLICATES[0] as number[]);
  const price = UPGRADE_PRICES[tierIndex] ?? (UPGRADE_PRICES[0] as number[]);
  const needed = owned.level >= dup.length ? dup[dup.length - 1] : dup[owned.level - 1];
  const cost = owned.level >= price.length ? price[price.length - 1] : price[owned.level - 1];
  return { needed: needed ?? 0, level: owned.level + 1, price: cost ?? 0 };
}

export function canUpgrade(save: BotUser, type: ItemType, owned: OwnedItem): { cards: boolean; money: boolean } {
  const next = nextLevel(type, owned);
  return { cards: owned.total > 0 && owned.current >= next.needed, money: save.money >= next.price };
}

/** Single fakecoin upgrade (`rpg/upgrade.ts#performUpgrade`, money path). */
export function upgrade(save: BotUser, type: ItemType, id: number, events: GameEvent[]): void {
  if (!getItem(type, id)) throw new GameError('unknownItem');
  const owned = save.summoned[type].owned.find((o) => o.id === id);
  if (!owned || owned.total < 1) throw new GameError('notOwned');
  const next = nextLevel(type, owned);
  if (owned.current < next.needed) throw new GameError('notEnoughCards', { needed: next.needed, have: owned.current });
  if (save.money < next.price) throw new GameError('notEnoughMoney', { needed: next.price, have: save.money });
  owned.current -= next.needed;
  owned.level += 1;
  save.money -= next.price;
  save.attack = teamPower(save);
  events.push({ type: 'itemUpgraded', itemType: type, id, level: owned.level, price: next.price });
}

/**
 * `rpg/upgrade.ts#runUpgradeCascade`: walks owned items in stored order,
 * levelling each while cards and budget allow, at most `maxPerItem` times.
 * Mutates `owned`; returns totals. Used for both preview (on a copy) and commit.
 */
export function upgradeCascade(type: ItemType, owned: OwnedItem[], budget: number, maxPerItem: number): { upgrades: number; spent: number } {
  let money = budget;
  let upgrades = 0;
  let spent = 0;
  for (const o of owned) {
    if (o.total < 1) continue;
    let count = 0;
    let next = nextLevel(type, o);
    while (count < maxPerItem && o.current >= next.needed && money >= next.price) {
      o.level += 1;
      o.current -= next.needed;
      money -= next.price;
      spent += next.price;
      upgrades += 1;
      count += 1;
      next = nextLevel(type, o);
    }
  }
  return { upgrades, spent };
}

export function previewUpgradeAll(save: BotUser, type: ItemType, maxPerItem: number): { upgrades: number; spent: number } {
  const copy = save.summoned[type].owned.map((o) => ({ ...o }));
  return upgradeCascade(type, copy, save.money, maxPerItem);
}

/** Upgrades the whole collection can buy right now, every type drawing on the same purse (type order). */
export function previewUpgradeCollection(save: BotUser): { upgrades: number; spent: number } {
  let money = save.money;
  let upgrades = 0;
  for (const type of ITEM_TYPES) {
    const copy = save.summoned[type].owned.map((o) => ({ ...o }));
    const r = upgradeCascade(type, copy, money, Infinity);
    upgrades += r.upgrades;
    money -= r.spent;
  }
  return { upgrades, spent: save.money - money };
}

/** Bulk upgrade with the quoted-price ceiling (bot fix 0bb9664). */
export function upgradeAll(save: BotUser, type: ItemType, maxPerItem: number, maxSpend: number, events: GameEvent[]): void {
  const { upgrades, spent } = upgradeCascade(type, save.summoned[type].owned, Math.min(save.money, maxSpend), maxPerItem);
  if (upgrades === 0) throw new GameError('notEnoughCards');
  save.money -= spent;
  save.attack = teamPower(save);
  events.push({ type: 'itemsUpgraded', itemType: type, upgrades, spent });
}
